// ===== BEGIN FILE: src/components/MobileChatInterface.tsx =====
/**
 * MobileChatInterface — full-screen mobile chat for a specific life area.
 *
 * Layout (top → bottom):
 *   ┌─ Crimson gradient header (← back, area icon + name) ─────────────┐
 *   │  Messages area (insight cards + regular bubbles, scrollable)      │
 *   │  Chip strip (horizontal scroll: Data · Remedies · Event chips)    │
 *   └─ Input bar (text field + send button) ────────────────────────────┘
 *
 * Uses useChatMessages for all GPT logic.
 * Uses useChatInsights for probability-engine chip handlers + insight cards.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Sheet,
  IconButton,
  Input,
  Typography,
  CircularProgress,
} from '@mui/joy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LIFE_AREAS, type LifeAreaKey } from '../utils/lifeAreas';
import { useChatMessages } from '../hooks/useChatMessages';
import {
  useChatInsights,
  type ChartInsightData,
  LIFE_AREA_INSIGHT_QUESTIONS,
  LIFE_AREA_CHIP_COLORS,
} from '../hooks/useChatInsights';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../utils/supabaseClient';
import { ChatMarkdown } from './chat/ChatMarkdown';
import { ChatOrbitalLoader } from './chat/ChatOrbitalLoader';
import ProbabilityTimelineChart from './widgets/ProbabilityTimelineChart';
import type { ChatSourceData } from '../utils/chatContext';
import { PersonalLifeAreaPanel } from './chat/ChatOutputPanel';

// Ordered list of all life area keys — used for prev/next navigation
const LIFE_AREA_KEYS = Object.keys(LIFE_AREAS) as LifeAreaKey[];

// ── Quick question suggestions per life area ───────────────────────────────
const LIFE_AREA_SUGGESTIONS: Partial<Record<LifeAreaKey, string[]>> = {
  self:       ['What defines my core personality?', 'What are my biggest strengths?', 'What blocks my growth?', 'What is my life purpose?'],
  health:     ['What does my chart say about my health?', 'Which planets affect my energy?', 'Any health cautions this period?', 'How to strengthen my vitality?'],
  wealth:     ['How strong is my wealth potential?', 'What blocks my financial growth?', 'Which dasha is best for finances?', 'How is my 2nd and 11th house?'],
  job:        ['How is my career this year?', 'Am I in the right profession?', 'When is a good time for a job change?', 'How strong is my 10th house?'],
  business:   ['Does my chart support entrepreneurship?', 'Best time to start a business?', 'What are my business strengths?', 'How is my 7th house for partnerships?'],
  marriage:   ['What does my chart say about marriage?', 'When is a favourable time for marriage?', 'What delays marriage in my chart?', 'What qualities suit me in a partner?'],
  children:   ['What does my chart say about children?', 'When is a good time for children?', 'How is my 5th house placed?', 'What does Jupiter indicate here?'],
  property:   ['When can I buy a property?', 'How is my 4th house?', 'Is now a good time for real estate?', 'What delays property purchase?'],
  education:  ['How does my chart support education?', 'Which subjects suit my chart?', 'When is favourable for exams?', 'What does Mercury say about me?'],
  mother:     ['How is my bond with my mother?', 'How is my 4th house for mother?', 'What does Moon indicate?', 'How can I support my mother?'],
  father:     ['How is my relationship with father?', 'What does my 9th house show?', 'What does Sun indicate for father?', 'How to improve bond with father?'],
};

interface MobileChatInterfaceProps {
  lifeArea: LifeAreaKey;
  contextData: ChatSourceData;
  userRole?: 'admin' | 'member';
  initialQuestion?: string;
  chartInsightData?: ChartInsightData;
  onBack: () => void;
  onGoHome?: () => void;
}

export default function MobileChatInterface({
  lifeArea,
  contextData,
  userRole,
  initialQuestion,
  chartInsightData,
  onBack,
  onGoHome,
}: MobileChatInterfaceProps) {
  // ── Life area navigation (internal state so user can swipe prev/next)
  const [currentLifeArea, setCurrentLifeArea] = useState<LifeAreaKey>(lifeArea);
  const area = LIFE_AREAS[currentLifeArea];
  const chipColors = LIFE_AREA_CHIP_COLORS[currentLifeArea] || LIFE_AREA_CHIP_COLORS['self'];
  // Prev / next indices (wraps around)
  const currentIdx = LIFE_AREA_KEYS.indexOf(currentLifeArea);
  const prevArea = LIFE_AREA_KEYS[(currentIdx - 1 + LIFE_AREA_KEYS.length) % LIFE_AREA_KEYS.length];
  const nextArea = LIFE_AREA_KEYS[(currentIdx + 1) % LIFE_AREA_KEYS.length];

  // ── Auth
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id || null));
  }, []);

  // ── Subscription (needed by useChatMessages)
  const { canAskChatQuestion, getRemainingQuota, getUpgradeMessage } = useSubscription(userId);

  // ── Chat messages
  const {
    messages, setMessages,
    input, setInput,
    pending,
    streamingContent,
    sendMessage,
    handleKeyDown,
  } = useChatMessages({
    contextData,
    selectedLifeArea: currentLifeArea,
    canAskChatQuestion,
    getRemainingQuota,
    getUpgradeMessage,
    initialQuestion,
    userRole,
  });

  // ── Chip insight handlers
  const {
    insightAttachments,
    insightPending,
    insightComputingId,
    insightSteps,
    handleEventInsight,
    handleDataInsight,
    handleLifeRemediesInsight,
  } = useChatInsights({
    chartInsightData,
    messages,
    setMessages,
  });

  // ── Auto-scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useCallback(() => {
    const doScroll = () => {
      const c = messagesContainerRef.current;
      if (c) c.scrollTop = c.scrollHeight;
    };
    // Fire immediately + at 100/300/600 ms to catch markdown table reflows
    // that happen after the initial paint (especially on mobile).
    doScroll();
    [100, 300, 600].forEach(ms => setTimeout(doScroll, ms));
  }, []);
  // Unified scroll: normal messages → scroll to bottom.
  // New insight card → scroll to its TOP immediately + retries for markdown reflow.
  // No second effect needed — avoids the "scroll down then jerk up" pattern.
  const prevAttachmentsSize = useRef(0);
  useEffect(() => {
    if (!messages.length && !streamingContent) return;

    if (insightAttachments.size > prevAttachmentsSize.current) {
      prevAttachmentsSize.current = insightAttachments.size;
      const scrollToCardTop = () => {
        const c = messagesContainerRef.current;
        if (!c) return;
        const cards = c.querySelectorAll('[data-insight-card]');
        const lastCard = cards[cards.length - 1] as HTMLElement | undefined;
        if (lastCard) {
          const containerRect = c.getBoundingClientRect();
          const cardRect = lastCard.getBoundingClientRect();
          c.scrollTop = c.scrollTop + (cardRect.top - containerRect.top) - 8;
        }
      };
      // t=0: card just rendered; retries handle markdown/table reflow on mobile
      [0, 100, 300, 600, 1000].forEach(ms => setTimeout(scrollToCardTop, ms));
    } else {
      scrollToBottom();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, streamingContent, insightAttachments]);

  // ── Auto-load on mount: if no chart data, send a GPT area overview.
  //    If chart data IS present, PersonalLifeAreaPanel renders as the initial view (no API call needed).
  const autoLoadFired = useRef(false);
  useEffect(() => {
    if (autoLoadFired.current || chartInsightData) return;
    autoLoadFired.current = true;
    // No chart data — fall back to GPT overview
    const overviewPrompt = `Give me a concise overview of ${area.label} based on my chart. ` +
      `Analyze relevant houses (${area.houses.map((h: number) => `H${h}`).join(', ')}) ` +
      `and include strengths, challenges, and current dasha timing.`;
    setTimeout(() => sendMessage(overviewPrompt, `🌟 ${area.label} Overview`), 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Navigate to a different life area
  const handleNavigateArea = useCallback((newArea: LifeAreaKey) => {
    setCurrentLifeArea(newArea);
    setMessages([]);
  }, [setMessages]);

  const handleSend = useCallback(() => {
    if (input.trim()) {
      setInputFocused(false);
      sendMessage();
    }
  }, [input, sendMessage]);

  // ── Copied state per message
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const handleCopy = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  }, []);

  // ── Input focus / suggestion state
  const [inputFocused, setInputFocused] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleInputFocus = useCallback(() => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setInputFocused(true);
    // Prevent Android from scrolling the document when keyboard opens
    window.scrollTo(0, 0);
  }, []);
  const handleInputBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => setInputFocused(false), 200);
  }, []);
  const handleSuggestionClick = useCallback((q: string) => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setInput(q);
    setInputFocused(false);
  }, [setInput]);
  const suggestions = LIFE_AREA_SUGGESTIONS[currentLifeArea] ?? [];
  const showSuggestions = inputFocused && !input.trim() && suggestions.length > 0;

  // ── When keyboard opens/closes: scroll latest message into view ──
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onVVResize = () => {
      setTimeout(() => {
        const c = messagesContainerRef.current;
        if (c) c.scrollTop = c.scrollHeight;
      }, 50);
    };
    vv.addEventListener('resize', onVVResize);
    return () => vv.removeEventListener('resize', onVVResize);
  }, []);

  const eventChips = LIFE_AREA_INSIGHT_QUESTIONS[currentLifeArea] ?? [];

  // ── Group-filtered event chips (related areas, same logic as desktop Trending Guidance)
  const _AREA_GROUP: Partial<Record<LifeAreaKey, string>> = {
    self: 'personal', health: 'personal',
    wealth: 'finance_career', job: 'finance_career', business: 'finance_career', property: 'finance_career', education: 'finance_career',
    marriage: 'relationship_family', children: 'relationship_family', mother: 'relationship_family', father: 'relationship_family',
  };
  const _GROUP_AREAS: Record<string, LifeAreaKey[]> = {
    personal:            ['health', 'children'],
    finance_career:      ['wealth', 'job', 'business', 'property'],
    relationship_family: ['marriage', 'children'],
  };
  const _selectedGroup = _AREA_GROUP[currentLifeArea] ?? null;
  const _groupAreas    = _selectedGroup ? _GROUP_AREAS[_selectedGroup] : null;
  const groupEventChips = (_groupAreas ?? [])
    .filter(a => a !== currentLifeArea)
    .flatMap(a => (LIFE_AREA_INSIGHT_QUESTIONS[a] ?? []).map(item => ({ item, area: a as LifeAreaKey })));

  // ── Chip pill style helper
  const chipPill = (extraSx: object, onClick: () => void, children: React.ReactNode) => (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: '20px',
        px: 1.75,
        py: 0.75,
        whiteSpace: 'nowrap',
        fontSize: '0.75rem',
        fontWeight: 600,
        flexShrink: 0,
        transition: 'all 0.15s',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        ...extraSx,
      }}
    >
      {children}
    </Box>
  );

  return (
    <Sheet
      sx={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999,
        bgcolor: 'background.body',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* ── ShreemAstro top bar — matches AppShell mobile header ──── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          background: 'linear-gradient(135deg, #fff8f8 0%, #fdf0f2 50%, #fff5f6 100%)',
          borderBottom: '1px solid rgba(184,48,80,0.14)',
          backdropFilter: 'saturate(180%) blur(10px)',
          flexShrink: 0,
          zIndex: 11,
        }}
      >
        <Box
          component="img"
          src="/Logo.png"
          alt="ShreemAstro"
          onClick={() => (onGoHome ?? onBack)()}
          sx={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0, cursor: 'pointer' }}
        />
        <Typography
          onClick={() => (onGoHome ?? onBack)()}
          sx={{
            fontFamily: '"Amita", serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            background: 'linear-gradient(135deg, #b83050 0%, #7a1a2e 45%, #4a0a18 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '1px',
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          ShreemAstro
          <Box component="sup" sx={{ fontSize: '0.6em', fontWeight: 400, letterSpacing: 0, verticalAlign: 'super', lineHeight: 0, WebkitTextFillColor: 'rgba(184,48,80,1)' }}>™</Box>
        </Typography>
      </Box>

      {/* ── Life area header ────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          background: 'linear-gradient(90deg, #7a1a2e 0%, #b83050 100%)',
          flexShrink: 0,
          zIndex: 10,
        }}
      >
        <IconButton
          variant="plain"
          onClick={onBack}
          sx={{ color: '#fff', minWidth: 36, minHeight: 36, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            fontSize: '1.05rem',
            fontWeight: 700,
            color: '#fff',
            fontFamily: '"Cinzel", serif',
            letterSpacing: '0.03em',
            flex: 1,
          }}
        >
          {area.icon} {area.label}
        </Typography>
        {/* ── Prev / Next area navigation ── */}
        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, alignItems: 'center' }}>
          <Box
            component="button"
            onClick={() => handleNavigateArea(prevArea)}
            title={LIFE_AREAS[prevArea].label}
            sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              border: '1.5px solid rgba(255,255,255,0.45)',
              bgcolor: 'rgba(255,255,255,0.14)',
              cursor: 'pointer', outline: 'none', p: 0,
              color: '#fff',
              transition: 'background 0.15s, border-color 0.15s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.26)', borderColor: 'rgba(255,255,255,0.75)' },
              '&:active': { bgcolor: 'rgba(255,255,255,0.36)', borderColor: '#fff' },
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: '0.85rem', ml: '3px', color: '#fff' }} />
          </Box>
          <Box
            component="button"
            onClick={() => handleNavigateArea(nextArea)}
            title={LIFE_AREAS[nextArea].label}
            sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              border: '1.5px solid rgba(255,255,255,0.45)',
              bgcolor: 'rgba(255,255,255,0.14)',
              cursor: 'pointer', outline: 'none', p: 0,
              color: '#fff',
              transition: 'background 0.15s, border-color 0.15s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.26)', borderColor: 'rgba(255,255,255,0.75)' },
              '&:active': { bgcolor: 'rgba(255,255,255,0.36)', borderColor: '#fff' },
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: '0.85rem', color: '#fff' }} />
          </Box>
        </Box>
      </Box>

      {/* ── Chip strip: sticky prominent section just below header ────── */}
      {chartInsightData && (
        <Box
          sx={{
            position: 'sticky',
            top: '112px',
            zIndex: 9,
            bgcolor: 'background.surface',
            borderBottom: '2px solid',
            borderColor: 'rgba(184,48,80,0.18)',
            boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
          }}
        >
          <Box sx={{ px: 1.5, pt: 0.75, pb: 0.25, display: 'flex', alignItems: 'center' }}>
            <Typography sx={{
              fontSize: '0.65rem',
              color: 'text.tertiary',
              fontFamily: '"Cinzel", serif',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>
              ✦ Explore Insights
            </Typography>
          </Box>
          <Box
            sx={{
              overflowX: 'auto',
              display: 'flex',
              gap: 0.75,
              px: 1.5,
              pb: 0.875,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {/* ── 1. Life-area-specific event chips (highlighted first) ── */}
            {eventChips.map((item, i) =>
              chipPill(
                { border: `1.5px solid ${chipColors.hborder}`, color: chipColors.color, bgcolor: chipColors.hbg, fontWeight: 700, boxShadow: `0 1px 5px ${chipColors.shadow}` },
                () => !pending && !insightPending && handleEventInsight(item.activity, item.displayLabel),
                item.label,
              )
            )}

            {/* ── 2. Related-group event chips ── */}
            {groupEventChips.map(({ item, area: a }, i) => {
              const clr = LIFE_AREA_CHIP_COLORS[a] ?? LIFE_AREA_CHIP_COLORS['job'];
              return chipPill(
                { border: `1px solid ${clr.border}`, color: clr.color, bgcolor: clr.bg },
                () => !pending && !insightPending && handleEventInsight(item.activity, item.displayLabel),
                item.label,
              );
            })}

            {/* ── 3. Pinned: Health & Vitality ── */}
            {currentLifeArea !== 'health' && _selectedGroup !== 'relationship_family' && chipPill(
              { border: '1px solid rgba(16,185,129,0.4)', color: '#065f46', bgcolor: 'rgba(16,185,129,0.08)' },
              () => !pending && !insightPending && handleEventInsight('surgery', 'What does my chart indicate about my health and vitality?'),
              '🌿 Health & Vitality',
            )}
            {/* ── 3. Pinned: Relationships ── */}
            {currentLifeArea !== 'marriage' && _selectedGroup !== 'finance_career' && chipPill(
              { border: '1px solid rgba(236,72,153,0.4)', color: '#9d174d', bgcolor: 'rgba(236,72,153,0.08)' },
              () => !pending && !insightPending && handleEventInsight('marriage', 'What does my chart say about relationships and marriage prospects?'),
              '💍 Relationships',
            )}

            {/* ── 4. Universal finance/career event chips ── */}
            {_selectedGroup !== 'relationship_family' && chipPill(
              { border: '1px solid rgba(212,175,55,0.5)', color: '#92400e', bgcolor: 'rgba(212,175,55,0.09)' },
              () => !pending && !insightPending && handleEventInsight('investment', 'How does my chart support wealth and financial growth?'),
              '💰 Financial Growth',
            )}
            {_selectedGroup !== 'relationship_family' && chipPill(
              { border: '1px solid rgba(99,102,241,0.4)', color: '#4338ca', bgcolor: 'rgba(99,102,241,0.08)' },
              () => !pending && !insightPending && handleEventInsight('business_start', 'How does my natal chart support my career and job prospects?'),
              '🎯 Career Strength',
            )}
            {_selectedGroup !== 'relationship_family' && chipPill(
              { border: '1px solid rgba(16,185,129,0.4)', color: '#065f46', bgcolor: 'rgba(16,185,129,0.08)' },
              () => !pending && !insightPending && handleEventInsight('business_start', 'What does my chart reveal about my potential for business success and entrepreneurship?'),
              '🏢 Business Success',
            )}

            {/* ── 5. Data insight chips ── */}
            {chipPill(
              { border: '1px solid rgba(16,185,129,0.4)', color: '#065f46', bgcolor: 'rgba(16,185,129,0.07)' },
              () => !pending && !insightPending && handleDataInsight('life_themes', 'Your Most Important Life Themes'),
              '🌟 Life Themes',
            )}
            {chipPill(
              { border: '1px solid rgba(139,92,246,0.4)', color: '#5b21b6', bgcolor: 'rgba(139,92,246,0.07)' },
              () => !pending && !insightPending && handleDataInsight('planet_strengths', 'Planetary Strengths & Balance'),
              '🪐 Planet Strengths',
            )}
            {chipPill(
              { border: '1px solid rgba(245,158,11,0.4)', color: '#92400e', bgcolor: 'rgba(245,158,11,0.07)' },
              () => !pending && !insightPending && handleDataInsight('powerful_patterns', 'Powerful Patterns in Your Chart'),
              '✨ Patterns',
            )}
            {chipPill(
              { border: '1px solid rgba(6,182,212,0.4)', color: '#0e7490', bgcolor: 'rgba(6,182,212,0.07)' },
              () => !pending && !insightPending && handleDataInsight('year_outlook', 'My Life Outlook for the Year'),
              '📅 Year Outlook',
            )}
            {chipPill(
              { border: '1px solid rgba(79,70,229,0.4)', color: '#3730a3', bgcolor: 'rgba(79,70,229,0.07)' },
              () => !pending && !insightPending && handleDataInsight('opportunities', 'Upcoming Opportunities & Caution Periods'),
              '🚀 Opportunities',
            )}

            {/* ── 6. Remedies (last) ── */}
            {chipPill(
              { border: '1px solid rgba(217,119,6,0.45)', color: '#78350f', bgcolor: 'rgba(251,191,36,0.08)' },
              () => !pending && !insightPending && handleLifeRemediesInsight(currentLifeArea),
              `🌿 ${area.label} Remedies`,
            )}
          </Box>
        </Box>
      )}

      {/* ── Messages ────────────────────────────────────────────────── */}
      {/* Outer box: scroll container only — no flex here (mobile Chrome scrollHeight bug) */}
      <Box
        ref={messagesContainerRef}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
      >
      {/* Inner box: flex column layout */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 1.5, pt: 1.5, pb: 2 }}>
        {/* ── Auto-insights panel (always visible, like desktop col-3) ─── */}
        {/* Shows This Week's Notifications + Things to Do/Avoid immediately on open.
            Stays pinned at top as the user scrolls through chip insights below. */}
        {chartInsightData ? (
          <PersonalLifeAreaPanel
            contextData={contextData}
            lifeArea={currentLifeArea}
            mobile={true}
          />
        ) : (
          /* No chart data yet — plain placeholder */
          <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
            <Typography sx={{ fontSize: '2rem', mb: 1 }}>{area.icon}</Typography>
            <Typography level="title-sm" sx={{ color: '#b83050', fontFamily: '"Cinzel", serif', mb: 0.5 }}>
              {area.label} Insights
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              Ask a question or tap a chip below for instant chart insights
            </Typography>
          </Box>
        )}

        {/* Divider between the auto-insights panel and chip/chat messages */}
        {messages.length > 0 && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 0.25,
          }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.62rem', fontFamily: '"Cinzel", serif', letterSpacing: '0.05em', flexShrink: 0 }}>
              YOUR INSIGHTS
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>
        )}

        {messages.map(msg => {
          const isInsightMsg = insightAttachments.has(msg.id);
          const isComputing = msg.content === '__computing__';

          // Computing / animated steps
          if (isComputing) {
            return (
              <Box key={msg.id} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box
                  sx={{
                    bgcolor: 'background.level1',
                    borderRadius: '16px 16px 16px 4px',
                    p: 1.75,
                    maxWidth: '90%',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size="sm" sx={{ '--CircularProgress-trackColor': 'rgba(184,48,80,0.15)', '--CircularProgress-progressColor': '#b83050' }} />
                    <Typography level="body-xs" sx={{ color: '#b83050', fontWeight: 600 }}>
                      Computing chart insights…
                    </Typography>
                  </Box>
                  {insightSteps.map((step, i) => (
                    <Typography
                      key={i}
                      level="body-xs"
                      sx={{ color: step.done ? 'text.primary' : 'text.tertiary', opacity: step.done ? 1 : 0.45, mb: 0.25 }}
                    >
                      {step.text}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          }

          // User bubble
          if (msg.role === 'user') {
            return (
              <Box key={msg.id} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #b83050 0%, #7a1a2e 100%)',
                    color: '#fff',
                    borderRadius: '18px 18px 4px 18px',
                    px: 2,
                    py: 1.25,
                    maxWidth: '82%',
                    fontSize: '0.95rem',
                    lineHeight: 1.5,
                    boxShadow: '0 2px 8px rgba(184,48,80,0.25)',
                  }}
                >
                  {msg.content}
                </Box>
              </Box>
            );
          }

          // Insight card (crimson bordered)
          if (isInsightMsg) {
            return (
              <Box
                key={msg.id}
                data-insight-card=""
                sx={{
                  border: '1.5px solid #b83050',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  mb: 0.5,
                  boxShadow: '0 2px 12px rgba(184,48,80,0.12)',
                }}
              >
                {/* Card header */}
                <Box
                  sx={{
                    background: 'linear-gradient(90deg, #7a1a2e 0%, #b83050 100%)',
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      fontFamily: '"Cinzel", serif',
                    }}
                  >
                    ✦ VEDIC CHART INSIGHT
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem' }}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => handleCopy(msg.content, msg.id)}
                    sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 28, minHeight: 28, '&:hover': { color: '#fff' } }}
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </Box>
                {/* Card body */}
                <Box sx={{ p: 2, fontSize: '0.95rem', lineHeight: 1.6 }}>
                  <ChatMarkdown content={msg.content} />
                  {(insightAttachments.get(msg.id)?.length ?? 0) > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <ProbabilityTimelineChart
                        windows={insightAttachments.get(msg.id)!}
                        yearsToShow={1}
                        compact={true}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }

          // Regular assistant bubble
          return (
            <Box key={msg.id} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 0.75 }}>
              <Box
                sx={{
                  bgcolor: 'background.level1',
                  borderRadius: '18px 18px 18px 4px',
                  px: 2,
                  py: 1.25,
                  maxWidth: '90%',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                }}
              >
                <ChatMarkdown content={msg.content} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => handleCopy(msg.content, msg.id)}
                    sx={{ color: copiedId === msg.id ? '#b83050' : 'text.tertiary', minWidth: 24, minHeight: 24 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.75rem' }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* Streaming */}
        {streamingContent && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box
              sx={{
                bgcolor: 'background.level1',
                borderRadius: '18px 18px 18px 4px',
                px: 2,
                py: 1.25,
                maxWidth: '90%',
                border: '1px solid',
                borderColor: 'divider',
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }}
            >
              <ChatMarkdown content={streamingContent} />
            </Box>
          </Box>
        )}

        {/* GPT loading */}
        {pending && !streamingContent && !insightPending && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box
              sx={{
                bgcolor: 'background.level1',
                borderRadius: '18px 18px 18px 4px',
                px: 2,
                py: 1.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ChatOrbitalLoader elapsedSeconds={0} />
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>{/* inner flex */}
      </Box>{/* messages scroll container */}

      {/* ── Input footer ──────────────────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.surface',
          borderTop: '1px solid',
          borderColor: 'divider',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
      {/* ── Question suggestions (slide up when input focused & empty) ── */}
      <Box
        sx={{
          maxHeight: showSuggestions ? '62px' : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.2s ease',
        }}
      >
        <Box
          sx={{
            overflowX: 'auto',
            display: 'flex',
            gap: 0.75,
            px: 1.5,
            py: 0.875,
            borderBottom: '1px solid',
            borderColor: 'divider',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {suggestions.map((q, i) => (
            <Box
              key={i}
              onMouseDown={e => { e.preventDefault(); handleSuggestionClick(q); }}
              onTouchEnd={e => { e.preventDefault(); handleSuggestionClick(q); }}
              sx={{
                cursor: 'pointer',
                flexShrink: 0,
                px: 1.5,
                py: 0.5,
                borderRadius: '16px',
                border: '1.5px solid rgba(184,48,80,0.25)',
                bgcolor: 'rgba(184,48,80,0.05)',
                fontSize: '0.78rem',
                fontWeight: 500,
                color: '#7a1a2e',
                whiteSpace: 'nowrap',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                '&:active': { bgcolor: 'rgba(184,48,80,0.12)' },
              }}
            >
              {q}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Input bar ───────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={`Ask Shreem about ${area.label}…`}
          disabled={pending || insightPending}
          sx={{
            flex: 1,
            fontSize: '1rem',
            '--Input-minHeight': '48px',
            borderRadius: '24px',
            border: '1.5px solid',
            borderColor: 'rgba(184,48,80,0.3)',
            '--Input-focusedHighlight': '#b83050',
            '&:hover': { borderColor: 'rgba(184,48,80,0.5)' },
          }}
        />
        <IconButton
          variant="solid"
          disabled={!input.trim() || pending || insightPending}
          onClick={handleSend}
          sx={{
            background: 'linear-gradient(135deg, #b83050 0%, #7a1a2e 100%)',
            color: '#fff',
            minWidth: 44,
            minHeight: 44,
            borderRadius: '50%',
            flexShrink: 0,
            '&:hover': { background: 'linear-gradient(135deg, #7a1a2e 0%, #b83050 100%)' },
            '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled', background: 'none' },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
      </Box>{/* footer */}
    </Sheet>
  );
}
// ===== END FILE: src/components/MobileChatInterface.tsx =====
